import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Box,
  Chip,
  IconButton,
  Modal,
  Button,
  Autocomplete,
  Typography,
  Paper,
  Badge,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Plus,
  X,
  Upload,
  Trash2,
  Star,
  StarOff,
  Image as ImageIcon,
} from "lucide-react";
import ProductSection from "../ProductSection";
import {
  useFetchBrandsQuery,
  useFetchCategoryTreeQuery,
  useCreateBrandMutation,
} from "../../../../../services/baseQuery";
import {
  errorToast,
  successToast,
} from "../../../../../services/toast.service";

const GENDERS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "BOTH", label: "Both" },
];

const COLORS = [
  { value: "Black", label: "Black", color: "#000000" },
  { value: "White", label: "White", color: "#FFFFFF" },
  { value: "Red", label: "Red", color: "#FF0000" },
  { value: "Blue", label: "Blue", color: "#0000FF" },
  { value: "Green", label: "Green", color: "#00FF00" },
  { value: "Yellow", label: "Yellow", color: "#FFFF00" },
  { value: "Brown", label: "Brown", color: "#A52A2A" },
];

const BasicDetails = ({ productData, updateProductData, errors }) => {
  // Fetch brands from API
  const {
    data: brandsData,
    isLoading: isBrandsLoading,
    isError: isBrandsError,
    refetch: refetchBrands,
  } = useFetchBrandsQuery({
    status: "ACTIVE",
    sort: "name_asc",
  });

  // Fetch categories from API
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useFetchCategoryTreeQuery({
    status: "ACTIVE",
    sort: "name_asc",
  });

  // Create brand mutation
  const [createBrand, { isLoading: isCreatingBrand }] =
    useCreateBrandMutation();

  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [newBrand, setNewBrand] = useState({
    name: "",
    description: "",
    brandtype: "DOMESTIC",
    image: null,
  });
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Transform API data
  const brands = brandsData?.data?.brands || [];
  const categories = transformCategories(
    categoriesData?.data?.categories || [],
  );

  function transformCategories(apiCategories) {
    const result = [];

    // Helper function to flatten the category tree
    const flattenCategories = (
      categories,
      parentName = null,
      parentId = null,
    ) => {
      categories?.forEach(category => {
        const displayName = parentName
          ? `${parentName} > ${category.name}`
          : category.name;

        result.push({
          id: category._id,
          name: displayName,
          status: category.status,
          parentId: parentId, // Track parent category ID
        });

        if (category.children && category.children.length > 0) {
          flattenCategories(category.children, displayName, category._id);
        }
      });
    };

    flattenCategories(apiCategories);
    return result;
  }

  const handleChange = e => {
    const { name, value } = e.target;
    updateProductData({ [name]: value });
  };

  const handleImageUpload = files => {
    if (!files || files.length === 0) return;

    const newImages = Array.from(files).map(file => ({
      file,
      url: URL.createObjectURL(file),
      isDefault: productData.images.length === 0, // First image is default
    }));

    updateProductData({
      images: [...productData.images, ...newImages],
    });
  };

  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files);
    }
  };

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files);
    }
  };

  const setDefaultImage = index => {
    const updatedImages = productData.images.map((img, i) => ({
      ...img,
      isDefault: i === index,
    }));

    updateProductData({ images: updatedImages });
  };

  const removeImage = index => {
    const updatedImages = [...productData.images];
    const removedImage = updatedImages.splice(index, 1)[0];

    if (removedImage.isDefault && updatedImages.length > 0) {
      updatedImages[0].isDefault = true;
    }

    updateProductData({ images: updatedImages });

    URL.revokeObjectURL(removedImage.url);
  };

  const handleBrandImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      setNewBrand({ ...newBrand, image: file });
    }
  };

  const handleAddBrand = async () => {
    try {
      if (!newBrand.name.trim()) {
        errorToast("Brand name is required");
        return;
      }

      // Create FormData object
      const submitFormData = new FormData();
      submitFormData.append("name", newBrand.name);
      submitFormData.append("brand_type", newBrand.brandtype);

      if (newBrand.description) {
        submitFormData.append("description", newBrand.description);
      }

      submitFormData.append(
        "orderValidationJson",
        JSON.stringify({
          validationType: "NONE",
        }),
      );

      if (newBrand.image) {
        submitFormData.append("image", newBrand.image);
      }

      const response = await createBrand(submitFormData).unwrap();

      if (response.success) {
        successToast("Brand created successfully");
        setShowAddBrandModal(false);

        // Refetch brands to update the list
        await refetchBrands();

        // Set the newly created brand as the selected brand
        if (response.data && response.data.brand) {
          updateProductData({ brand: response.data.brand._id });
        }

        // Reset form
        setNewBrand({
          name: "",
          description: "",
          brandtype: "DOMESTIC",
          image: null,
        });
      } else {
        errorToast(response.message || "Failed to create brand");
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      errorToast(error.data?.error?.message || "Failed to create brand");
    }
  };

  const handleCategoryChange = e => {
    const categoryId = e.target.value;
    const selectedCategory = categories.find(cat => cat.id === categoryId);

    if (selectedCategory) {
      if (selectedCategory.parentId) {
        // Case 2: Child category selected
        updateProductData({
          categories: [selectedCategory.parentId, categoryId], // Include parent and child
          primary_category: categoryId, // Primary is the selected child
        });
      } else {
        // Case 1: Parent category selected
        updateProductData({
          categories: [categoryId], // Only include parent
          primary_category: categoryId, // Primary is the parent
        });
      }
    } else {
      // No category selected
      updateProductData({
        categories: [],
        primary_category: "",
      });
    }
  };

  return (
    <div className="space-y-8">
      <ProductSection data="Basic Information" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Product Code"
            name="productCode"
            value={productData.productCode}
            onChange={handleChange}
            fullWidth
            size="small"
            error={!!errors.productCode}
            helperText={errors.productCode}
            required
            InputProps={{
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Product Name"
            name="name"
            value={productData.name}
            onChange={handleChange}
            fullWidth
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            required
            InputProps={{
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" error={!!errors.color} required>
            <Autocomplete
              options={COLORS}
              getOptionLabel={option => option.label}
              value={COLORS.find(c => c.value === productData.color) || null}
              onChange={(e, newValue) => {
                updateProductData({ color: newValue ? newValue.value : "" });
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: option.color,
                      border: "1px solid #E5E7EB",
                    }}
                  />
                  {option.label}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Color"
                  error={!!errors.color}
                  helperText={errors.color}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="flex items-center gap-2">
            <FormControl fullWidth size="small" error={!!errors.brand} required>
              {isBrandsLoading ? (
                <TextField
                  label="Brand"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <CircularProgress size={20} color="inherit" />
                    ),
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              ) : (
                <Autocomplete
                  options={brands}
                  getOptionLabel={option => option.name}
                  value={brands.find(b => b._id === productData.brand) || null}
                  onChange={(e, newValue) => {
                    updateProductData({ brand: newValue ? newValue._id : "" });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Brand"
                      error={!!errors.brand}
                      helperText={errors.brand}
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          borderRadius: "0.5rem",
                          fontFamily: "Outfit",
                        },
                      }}
                      InputLabelProps={{
                        sx: {
                          fontFamily: "Outfit",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {option.image ? (
                        <img
                          src={option.image}
                          alt={option.name}
                          style={{
                            width: 24,
                            height: 24,
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/24";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: "#f3f4f6",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                      <span>{option.name}</span>
                    </Box>
                  )}
                />
              )}
            </FormControl>
            <IconButton
              onClick={() => setShowAddBrandModal(true)}
              sx={{
                border: "2px solid #5652B0",
                borderRadius: "0.5rem",
                padding: "8px",
                color: "#5652B0",
                backgroundColor: "#F9F8FE",
              }}>
              <Plus size={20} />
            </IconButton>
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            size="small"
            error={!!errors.category}
            required>
            <InputLabel id="category-label" sx={{ fontFamily: "Outfit" }}>
              Category
            </InputLabel>
            {isCategoriesLoading ? (
              <Select
                labelId="category-label"
                label="Category"
                disabled
                sx={{
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                }}
                endAdornment={<CircularProgress size={20} color="inherit" />}
              />
            ) : (
              <Select
                labelId="category-label"
                name="category"
                value={productData.primary_category || ""}
                onChange={handleCategoryChange}
                label="Category"
                sx={{
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                }}>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {errors.category && (
              <FormHelperText>{errors.category}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" error={!!errors.gender} required>
            <InputLabel id="gender-label" sx={{ fontFamily: "Outfit" }}>
              Gender
            </InputLabel>
            <Select
              labelId="gender-label"
              name="gender"
              value={productData.gender}
              onChange={handleChange}
              label="Gender"
              sx={{
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              }}>
              {GENDERS.map(gender => (
                <MenuItem key={gender.value} value={gender.value}>
                  {gender.label}
                </MenuItem>
              ))}
            </Select>
            {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="HSN/SAC"
            name="hsn_sac"
            value={productData.hsn_sac}
            onChange={handleChange}
            fullWidth
            size="small"
            error={!!errors.hsn_sac}
            helperText={errors.hsn_sac}
            required
            InputProps={{
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="tax-type-label" sx={{ fontFamily: "Outfit" }}>
              Tax Type
            </InputLabel>
            <Select
              labelId="tax-type-label"
              name="taxType"
              value={productData.taxType}
              onChange={handleChange}
              label="Tax Type"
              sx={{
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              }}>
              <MenuItem value="INCLUSIVE">Inclusive</MenuItem>
              <MenuItem value="EXCLUSIVE">Exclusive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Tax Rate (%)"
            name="taxRate"
            type="number"
            value={productData.taxRate}
            onChange={handleChange}
            fullWidth
            size="small"
            InputProps={{
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>
      </Grid>

      <ProductSection data="Product Images" />

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center mb-4 transition-colors ${
            dragActive ? "border-[#5652B0] bg-[#F9F8FE]" : "border-gray-300"
          }`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          <ImageIcon size={40} className="mx-auto mb-2 text-gray-400" />
          <Typography variant="body1" className="font-medium mb-1">
            Drag and drop product images here
          </Typography>
          <Typography variant="body2" className="text-gray-500 mb-3">
            or
          </Typography>
          <button
            onClick={() => fileInputRef.current.click()}
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] inline-flex items-center gap-2">
            <Upload size={16} />
            Browse Images
          </button>
          <Typography variant="caption" className="block mt-2 text-gray-500">
            Supported formats: JPG, PNG, WEBP • Max size: 5MB • Recommended
            ratio: 1:1
          </Typography>
        </div>

        {productData.images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {productData.images.map((image, index) => (
              <Paper
                key={index}
                elevation={1}
                className="relative group"
                sx={{ aspectRatio: "1/1", overflow: "hidden" }}>
                <img
                  src={image.url}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Tooltip
                    title={
                      image.isDefault ? "Default Image" : "Set as Default"
                    }>
                    <IconButton
                      size="small"
                      onClick={() => setDefaultImage(index)}
                      sx={{
                        color: image.isDefault ? "#FFD700" : "white",
                        "&:hover": { color: "#FFD700" },
                      }}>
                      {image.isDefault ? (
                        <Star size={20} />
                      ) : (
                        <StarOff size={20} />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Remove Image">
                    <IconButton
                      size="small"
                      onClick={() => removeImage(index)}
                      sx={{
                        color: "white",
                        "&:hover": { color: "#FF5252" },
                      }}>
                      <X size={20} />
                    </IconButton>
                  </Tooltip>
                </div>

                {image.isDefault && (
                  <div className="absolute top-2 left-2 bg-[#5652B0] text-white text-xs py-1 px-2 rounded-md">
                    Default
                  </div>
                )}
              </Paper>
            ))}

            <Paper
              elevation={1}
              className="flex items-center justify-center cursor-pointer hover:bg-gray-50"
              sx={{ aspectRatio: "1/1" }}
              onClick={() => fileInputRef.current.click()}>
              <div className="text-center">
                <Plus size={24} className="mx-auto mb-1 text-gray-400" />
                <Typography variant="caption" className="text-gray-500">
                  Add More
                </Typography>
              </div>
            </Paper>
          </div>
        )}
      </div>

      <ProductSection data="Brief Description" />

      <TextField
        label="Brief Description"
        name="description"
        value={productData.description}
        onChange={handleChange}
        fullWidth
        multiline
        rows={4}
        placeholder="Enter a brief description of the product..."
        InputProps={{
          sx: {
            borderRadius: "0.5rem",
            fontFamily: "Outfit",
          },
        }}
        InputLabelProps={{
          sx: {
            fontFamily: "Outfit",
          },
        }}
      />

      {/* Add Brand Modal */}
      <Modal
        open={showAddBrandModal}
        onClose={() => setShowAddBrandModal(false)}
        aria-labelledby="add-brand-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold font-outfit">Add New Brand</h2>
            <IconButton onClick={() => setShowAddBrandModal(false)}>
              <X size={20} />
            </IconButton>
          </div>

          <div className="space-y-4">
            <TextField
              label="Brand Name"
              value={newBrand.name}
              onChange={e => setNewBrand({ ...newBrand, name: e.target.value })}
              fullWidth
              size="small"
              required
              InputProps={{
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}
            />

            <TextField
              label="Description (Optional)"
              value={newBrand.description}
              onChange={e =>
                setNewBrand({ ...newBrand, description: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              size="small"
              InputProps={{
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="brand-type-label" sx={{ fontFamily: "Outfit" }}>
                Brand Type
              </InputLabel>
              <Select
                labelId="brand-type-label"
                value={newBrand.brandtype}
                onChange={e =>
                  setNewBrand({ ...newBrand, brandtype: e.target.value })
                }
                label="Brand Type"
                sx={{
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                }}>
                <MenuItem value="DOMESTIC">Domestic</MenuItem>
                <MenuItem value="INTERNATIONAL">International</MenuItem>
              </Select>
            </FormControl>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              {newBrand.image ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(newBrand.image)}
                    alt="Brand logo"
                    className="h-32 mx-auto object-contain"
                  />
                  <button
                    onClick={() => setNewBrand({ ...newBrand, image: null })}
                    className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md">
                    <Trash2 size={16} className="text-red-500" />
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-gray-600 mb-2">Upload brand logo</p>
                  <input
                    type="file"
                    id="brand-logo"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBrandImageSelect}
                  />
                  <label
                    htmlFor="brand-logo"
                    className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] cursor-pointer inline-block">
                    Browse Files
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowAddBrandModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isCreatingBrand}>
                Cancel
              </button>
              <button
                onClick={handleAddBrand}
                disabled={!newBrand.name || isCreatingBrand}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {isCreatingBrand ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Creating...
                  </>
                ) : (
                  "Add Brand"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default BasicDetails;
